import { useCallback, useEffect, useRef, useState } from "react";
import { get, isNil } from "lodash";
import { useMutation } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import { DRAFT_ERRORS, draftErrorOf } from "./draft-errors";

export const SAVE_OUTCOMES = {
  SAVED: "SAVED",
  UNCHANGED: "UNCHANGED",
  CONFLICT: "CONFLICT",
  FAILED: "FAILED",
};

const SNAPSHOT_DELAY = 250;
const SAVE_DELAY = 550;

const useDraftAutosave = ({
  process: { id },
  nodes,
  edges,
  snapshot,
  onConflict,
}) => {
  const [saveProcessDraft] = useMutation(graphql.mutations.SAVE_PROCESS_DRAFT, {
    onError: () => undefined,
  });
  const [current, setCurrent] = useState(null);
  const [saved, setSaved] = useState(null);
  const [revision, setRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [holds, setHolds] = useState(0);
  const savedRef = useRef(null);
  const revisionRef = useRef(0);
  const holdsRef = useRef(0);
  const conflictedRef = useRef(false);
  const [conflicted, setConflicted] = useState(false);
  const queue = useRef(Promise.resolve(SAVE_OUTCOMES.UNCHANGED));
  const onConflictRef = useRef(onConflict);
  onConflictRef.current = onConflict;

  const reset = useCallback(({ statuses, revision: next }) => {
    const text = JSON.stringify(statuses);
    savedRef.current = text;
    revisionRef.current = next;
    setSaved(text);
    setRevision(next);
    setCurrent({ statuses, text });
    setFailed(null);
    setRefreshFailed(false);
    conflictedRef.current = false;
    setConflicted(false);
  }, []);

  const send = useCallback(
    async (statuses) => {
      const text = JSON.stringify(statuses);
      if (text === savedRef.current) return SAVE_OUTCOMES.UNCHANGED;
      setSaving(true);
      try {
        const result = await saveProcessDraft({
          variables: {
            where: { process: { id } },
            data: {
              graph: { processStatuses: statuses },
              revision: revisionRef.current,
            },
          },
        });
        const row = get(result, "data.saveProcessDraft");
        if (isNil(row)) {
          if (
            draftErrorOf(get(result, "errors")) === DRAFT_ERRORS.DRAFT_CONFLICT
          ) {
            conflictedRef.current = true;
            setConflicted(true);
            return SAVE_OUTCOMES.CONFLICT;
          }
          setFailed(text);
          return SAVE_OUTCOMES.FAILED;
        }
        savedRef.current = text;
        revisionRef.current = row.revision;
        setSaved(text);
        setRevision(row.revision);
        setFailed(null);
        return SAVE_OUTCOMES.SAVED;
      } catch (_error) {
        setFailed(text);
        return SAVE_OUTCOMES.FAILED;
      } finally {
        setSaving(false);
      }
    },
    [saveProcessDraft, id],
  );

  const enqueue = useCallback(
    (statuses) => {
      const next = queue.current.then(() => send(statuses));
      queue.current = next;
      return next;
    },
    [send],
  );

  useEffect(() => {
    if (isNil(saved)) return undefined;
    const timer = setTimeout(() => {
      const statuses = snapshot(nodes, edges);
      const text = JSON.stringify(statuses);
      setCurrent((previous) =>
        get(previous, "text") === text ? previous : { statuses, text },
      );
    }, SNAPSHOT_DELAY);
    return () => clearTimeout(timer);
  }, [nodes, edges, snapshot, saved]);

  const dirty = !isNil(current) && current.text !== saved;
  const failing = (dirty && current.text === failed) || refreshFailed;

  useEffect(() => {
    if (!dirty || failing || saving || holds > 0 || conflicted) {
      return undefined;
    }
    const timer = setTimeout(async () => {
      if (holdsRef.current > 0 || conflictedRef.current) return;
      const outcome = await enqueue(current.statuses);
      if (outcome !== SAVE_OUTCOMES.CONFLICT) return;
      try {
        const refreshed = await onConflictRef.current();
        if (refreshed === false) {
          setFailed(current.text);
          setRefreshFailed(true);
        }
      } catch (_error) {
        setFailed(current.text);
        setRefreshFailed(true);
      }
    }, SAVE_DELAY);
    return () => clearTimeout(timer);
  }, [dirty, failing, saving, holds, conflicted, current, enqueue]);

  const flush = () => enqueue(snapshot(nodes, edges));

  const failRefresh = () => setRefreshFailed(true);

  const retry = () => {
    conflictedRef.current = false;
    setConflicted(false);
    setRefreshFailed(false);
    setFailed(null);
  };

  const readRevision = () => revisionRef.current;

  const hold = async (task) => {
    holdsRef.current += 1;
    setHolds((count) => count + 1);
    try {
      await queue.current;
      const result = await task();
      return result;
    } finally {
      holdsRef.current -= 1;
      setHolds((count) => count - 1);
    }
  };

  return {
    current,
    revision,
    saving,
    pending: dirty && !failing && !conflicted,
    failing,
    reset,
    flush,
    failRefresh,
    retry,
    hold,
    readRevision,
  };
};

export default useDraftAutosave;
